import React, { Suspense, lazy, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePrefersReducedMotion } from '../../hooks/useReveal';

const PageScene = lazy(() => import('./PageScene'));

/* Route → 3D scene variant */
function variantFor(pathname) {
  if (pathname.startsWith('/services')) return 'servers';
  if (pathname.startsWith('/technology')) return 'services';
  if (pathname.startsWith('/industries')) return 'hub';
  if (pathname.startsWith('/our-process')) return 'pipeline';
  if (pathname.startsWith('/about')) return 'stack';
  if (pathname.startsWith('/solutions/ai')) return 'services';
  if (pathname.startsWith('/solutions')) return 'stack';
  if (pathname.startsWith('/contact')) return 'hub';
  if (pathname.startsWith('/case-studies')) return 'services';
  if (pathname.startsWith('/careers')) return 'pipeline';
  if (pathname.startsWith('/privacy') || pathname.startsWith('/terms')) return 'servers';
  if (pathname.startsWith('/blog')) return 'pipeline';
  return 'stack';
}

function canUseWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch {
    return false;
  }
}

/**
 * Injects a WebGL scene into the page's `.page-hero` banner (if it has one),
 * so every inner page gets a 3D hero without touching each page file.
 */
function PageHero3D({ pathname }) {
  const [host, setHost] = useState(null);
  const [visible, setVisible] = useState(true);
  const [ok, setOk] = useState(false);
  const [lite, setLite] = useState(false);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    setOk(!navigator.connection?.saveData && canUseWebGL());
    setLite(window.innerWidth < 768 || (navigator.hardwareConcurrency || 8) <= 4);
  }, []);

  /* Find the hero (lazy routes mount late) and attach a host node */
  useEffect(() => {
    if (!ok) return undefined;
    let node = null;
    const attach = () => {
      if (node) return;
      const hero = document.querySelector('main .page-hero, main .page-header, main .placeholder-page');
      if (!hero || hero.querySelector(':scope > .ph3d')) return;
      node = document.createElement('div');
      node.className = 'ph3d';
      node.setAttribute('aria-hidden', 'true');
      hero.insertBefore(node, hero.firstChild);
      setHost(node);
    };
    const timers = [30, 300, 800, 1600].map((ms) => setTimeout(attach, ms));
    return () => {
      timers.forEach(clearTimeout);
      if (node) node.remove();
      setHost(null);
    };
  }, [pathname, ok]);

  /* Pause rendering while the hero is off-screen */
  useEffect(() => {
    if (!host || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting));
    io.observe(host);
    return () => io.disconnect();
  }, [host]);

  if (!host) return null;
  return createPortal(
    <Suspense fallback={null}>
      <PageScene variant={variantFor(pathname)} reduced={reduced} lite={lite} active={visible} />
    </Suspense>,
    host
  );
}

export default PageHero3D;
